/**
 * Application Entry Point
 *
 * AUTH MECHANISM OVERVIEW:
 * ========================
 * This app uses Cognito with PKCE (Proof Key for Code Exchange) for authentication.
 * The flow has two phases that happen on separate page loads:
 *
 * PHASE 1 — Login redirect (handled by AuthGuard):
 *   User visits app → no token → AuthGuard redirects to Cognito login page →
 *   User authenticates via Federate (Amazon SSO) → Cognito redirects back to
 *   /callback?code=AUTHORIZATION_CODE
 *
 * PHASE 2 — Code exchange (handled here in index.tsx, BEFORE React mounts):
 *   Page loads at /callback?code=xxx → handleCallbackIfNeeded() runs →
 *   exchanges code for id_token via Cognito /oauth2/token endpoint →
 *   saves token to sessionStorage → redirects to / (clean URL) →
 *   on fresh / load: token exists → AuthGuard renders app → done.
 *
 * WHY code exchange is here (not in a React component):
 *   React StrictMode double-mounts components in dev. The authorization code
 *   is single-use — if exchanged twice, the second attempt fails. By handling
 *   it before React mounts, we guarantee exactly one exchange attempt regardless
 *   of StrictMode behavior.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import "@cloudscape-design/global-styles/index.css";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import { store } from "./store/store";
import { App } from "./components/layout/app/App";
import { AuthGuard } from "./components/layout/auth/AuthGuard";
import { exchangeCode, setToken, consumePostLoginRedirect } from "./auth";

applyMode(Mode.Light);

/**
 * Handles the OAuth callback (/callback?code=xxx) BEFORE React mounts.
 *
 * Cases:
 * - No ?code= param → returns immediately (normal page load)
 * - ?code= present, exchange succeeds → token saved, redirects to /
 * - ?code= present, exchange fails → no token saved, redirects to /
 *   (AuthGuard will detect missing token and restart login flow)
 *
 * In ALL cases where ?code= is present, we redirect to / to clean the URL.
 * This function either redirects (never returns) or returns void.
 */
async function handleCallbackIfNeeded(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');

  // Not a callback page — let React handle it
  if (!code) return;

  // Attempt to exchange the authorization code for an id_token
  try {
    const idToken = await exchangeCode(code);
    setToken(idToken);
  } catch {
    // Exchange failed (code expired, PKCE verifier lost, etc.)
    // Don't show error here — just redirect to / and let AuthGuard handle it.
    // AuthGuard will find no token and restart the login flow cleanly.
  }

  // Redirect back to the deep link stashed before the login redirect
  // (falls back to '/'). Removes ?code= from URL, triggers fresh page load.
  // Uses replace() so /callback doesn't stay in browser history.
  // If the exchange failed, AuthGuard will find no token on the target page
  // and restart the login flow, re-stashing the same deep link.
  window.location.replace(window.location.origin + consumePostLoginRedirect());
  // Execution effectively stops here — browser navigates away.
}

/**
 * Main entry — runs the callback handler first, then renders React.
 * If handleCallbackIfNeeded redirects, we never reach root.render().
 */
async function main() {
  await handleCallbackIfNeeded();

  // If we're here, it's a normal page load (not a callback).
  // React mounts. AuthGuard checks for a valid token before rendering <App />.
  const container = document.getElementById("root");
  const root = createRoot(container!);
  root.render(
    <StrictMode>
      <Provider store={store}>
        <BrowserRouter>
          <AuthGuard>
            <App />
          </AuthGuard>
        </BrowserRouter>
      </Provider>
    </StrictMode>
  );
}

main();
